import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Plus, X, AlertCircle } from 'lucide-react';
import { ValidationRule, validateField } from '../../utils/propertyValidation';

export interface PropertyFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'multiselect' | 'date' | 'datetime' | 'password' | 'email' | 'url' | 'expression' | 'object' | 'array';
  placeholder?: string;
  description?: string;
  defaultValue?: any;
  options?: { value: string; label: string }[];
  validation?: ValidationRule;
  disabled?: boolean;
  rows?: number;
  condition?: {
      field: string;
      operator: 'equals' | 'notEquals' | 'contains';
      value: any;
  };
  itemType?: 'text' | 'number'; // For array type
}

interface PropertyFieldProps {
  config: PropertyFieldConfig;
  value: any;
  onChange: (key: string, value: any) => void;
  error?: string;
}

export const PropertyField: React.FC<PropertyFieldProps> = ({ config, value, onChange, error: externalError }) => {
  const [internalError, setInternalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [jsonText, setJsonText] = useState('');

  const currentValue = value !== undefined ? value : (config.defaultValue ?? '');

  // Sync JSON text for object type when external value changes
  useEffect(() => {
    if (config.type === 'object' && value) {
        try {
            setJsonText(JSON.stringify(value, null, 2));
        } catch (e) {
            setJsonText('{}');
        }
    }
  }, [value, config.type]);

  const handleChange = (val: any) => {
    // Validate immediately
    if (config.validation) {
        const err = validateField(val, config.validation);
        setInternalError(err);
    } else {
        setInternalError(null);
    }
    onChange(config.name, val);
  };

  const handleJsonChange = (text: string) => {
      setJsonText(text);
      try {
          const parsed = JSON.parse(text);
          setInternalError(null);
          onChange(config.name, parsed);
      } catch (e) {
          setInternalError("Invalid JSON format");
      }
  };

  const handleArrayAdd = () => {
      const newItem = config.itemType === 'number' ? 0 : '';
      const newArray = Array.isArray(currentValue) ? [...currentValue, newItem] : [newItem];
      handleChange(newArray);
  };

  const handleArrayRemove = (index: number) => {
      if (!Array.isArray(currentValue)) return;
      const newArray = currentValue.filter((_, i) => i !== index);
      handleChange(newArray);
  };

  const handleArrayItemChange = (index: number, val: any) => {
      if (!Array.isArray(currentValue)) return;
      const newArray = [...currentValue];
      newArray[index] = val;
      handleChange(newArray);
  };

  const inputClasses = "w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 placeholder-slate-400 transition-all font-sans";

  const renderInput = () => {
    switch (config.type) {
      case 'textarea':
        return (
          <textarea
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            rows={config.rows || 3}
            placeholder={config.placeholder}
            disabled={config.disabled}
            className={inputClasses}
          />
        );
      case 'expression':
        return (
          <div className="relative">
             <textarea
                value={currentValue}
                onChange={(e) => handleChange(e.target.value)}
                rows={config.rows || 2}
                placeholder={config.placeholder || "e.g. variables.amount > 100"}
                disabled={config.disabled}
                className="w-full px-3 py-2 text-sm border rounded-lg border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 focus:outline-none focus:ring-2 focus:ring-amber-500/50 font-mono text-slate-700 dark:text-slate-300 placeholder-amber-400/50"
            />
            <div className="absolute top-1 right-2 text-[10px] text-amber-600 font-semibold pointer-events-none">JS</div>
          </div>
        );
      case 'object':
        return (
            <div>
                <textarea
                    value={jsonText}
                    onChange={(e) => handleJsonChange(e.target.value)}
                    rows={config.rows || 5}
                    placeholder="{}"
                    className={`w-full px-3 py-2 text-xs border rounded-lg bg-slate-50 dark:bg-slate-900 font-mono focus:outline-none focus:ring-1 ${internalError ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-700 focus:ring-teal-500 focus:border-teal-500'}`}
                />
            </div>
        );
      case 'array':
        return (
            <div className="space-y-2">
                {(Array.isArray(currentValue) ? currentValue : []).map((item: any, index: number) => (
                    <div key={index} className="flex gap-2">
                         <input
                            type={config.itemType === 'number' ? 'number' : 'text'}
                            value={item}
                            onChange={(e) => handleArrayItemChange(index, config.itemType === 'number' ? Number(e.target.value) : e.target.value)}
                            className={inputClasses}
                         />
                         <button 
                            onClick={() => handleArrayRemove(index)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                         >
                             <X size={14} />
                         </button>
                    </div>
                ))}
                <button 
                    onClick={handleArrayAdd}
                    className="flex items-center gap-1.5 text-xs text-teal-600 dark:text-teal-400 font-medium hover:text-teal-700 px-1 py-1"
                >
                    <Plus size={12} /> Add Item
                </button>
            </div>
        );
      case 'boolean':
        return (
           <div className="flex items-center gap-2">
               <button
                  type="button"
                  onClick={() => handleChange(!currentValue)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                      currentValue ? 'bg-teal-600' : 'bg-slate-200 dark:bg-slate-700'
                  }`}
               >
                   <span
                      className={`${
                          currentValue ? 'translate-x-5' : 'translate-x-1'
                      } inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow-sm`}
                   />
               </button>
               <span className="text-sm text-slate-600 dark:text-slate-400 select-none">{currentValue ? 'Yes' : 'No'}</span>
           </div>
        );
      case 'select':
        return (
            <select
                value={currentValue}
                onChange={(e) => handleChange(e.target.value)}
                disabled={config.disabled}
                className={inputClasses}
            >
                {config.options?.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
      case 'password':
          return (
              <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={currentValue}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder={config.placeholder}
                    disabled={config.disabled}
                    className={`${inputClasses} pr-8`}
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
                  >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
              </div>
          );
      case 'number':
           return (
              <input
                type="number"
                value={currentValue}
                onChange={(e) => handleChange(Number(e.target.value))}
                placeholder={config.placeholder}
                disabled={config.disabled}
                className={inputClasses}
              />
           );
      // Fallback to text
      default: 
        return (
            <input
                type={config.type === 'email' ? 'email' : config.type === 'url' ? 'url' : 'text'}
                value={currentValue}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={config.placeholder}
                disabled={config.disabled}
                className={inputClasses}
            />
        );
    }
  };

  return (
    <div className="space-y-1.5 mb-5">
      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center justify-between">
        <span>
            {config.label}
            {config.validation?.required && <span className="text-teal-600 ml-1">*</span>}
        </span>
        {config.type === 'expression' && (
             <span title="Supports JavaScript expressions" className="text-slate-400 cursor-help"><AlertCircle size={10}/></span>
        )}
      </label>
      {renderInput()}
      {config.description && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{config.description}</p>}
      {(externalError || internalError) && (
          <p className="text-[11px] text-red-600 font-medium mt-1">{externalError || internalError}</p>
      )}
    </div>
  );
};